import React, { useEffect } from "react";
import styled from "styled-components";
import {
  colors2,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_HEADLINE,
} from "oolib";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { isFeatureDisabledOnProd } from "@/utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";
import {
  getIntegrationInterface,
  getIntegrationIcon,
  DefaultIconFallback,
} from "../IntegrationInterfaces";

// Styled components for the tiles
const TilesContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  padding-right: 8px; /* breathing room so tiles don't sit under the scrollbar */
`;

const SourceTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  min-width: 160px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? colors2.blue : colors2.grey10)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $isSelected }) => ($isSelected ? colors2.blue5 : colors2.white)};

  &:hover {
    border-color: ${colors2.blue};
    background: ${colors2.blue5};
  }
`;

const TileIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${colors2.grey5};

  svg {
    width: 32px;
    height: 32px;
    color: ${colors2.grey60};
  }
`;

const TileName = styled(UI_BODY_SEMIBOLD_SM_DF)`
  text-align: center;
`;

// CSV/Excel icon (using a simple file icon representation)
export const CsvExcelIcon = ({ size } = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/**
 * DataSourceSelector Component
 *
 * Allows user to select between CSV/Excel upload or external integrations (Fathom, etc.)
 * Uses the IntegrationInterfaces registry to render the appropriate interface and icon
 * for each integration.
 *
 * @param {string|null} selectedSource - Currently selected integration adapter id (null shows the tiles). Owned by the parent (DirectDataImport) so it survives the preview screen and seeds from the URL on OAuth return / Activate deep-links.
 * @param {Function} setSelectedSource - Setter for selectedSource (parent state).
 * @param {Function} onSelectCsvExcel - Called when user selects CSV/Excel (shows file upload)
 * @param {Function} onExternalSourceSubmit - Called with { sourceId, apiKey, adapterOptions } when external source is ready
 * @param {boolean} isLoading - Shows loading state on submit button
 */
export const DataSourceSelector = ({
  selectedSource,
  setSelectedSource,
  onSelectCsvExcel,
  onExternalSourceSubmit,
  isLoading,
  submitError,
  onClearSubmitError,
  entryPoint,
}) => {

  // Auto-open a provider's tile when the URL says we should:
  //   - ?externalConnectionResult=…&provider=…   (user just returned from OAuth)
  //   - ?integrationActivation=…&provider=…      (user clicked Activate on the
  //                                                Integrations tab and was
  //                                                punted here for setup)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider");
    const result = params.get("externalConnectionResult");
    const integrationActivation = params.get("integrationActivation");
    if (provider && (result || integrationActivation)) {
      setSelectedSource(provider);
    }
  }, [setSelectedSource]);

  // Get external adapters config from platform configs
  const platformConfigs = useGetQueryData("platformConfigs");
  const allAdapters =
    platformConfigs?._DirectDataImport?.EXTERNAL_IMPORT_ADAPTERS_CONFIG || [];

  // Filter out adapters disabled on prod. Use the tenant-aware helper (not a
  // plain .includes) so per-tenant whitelist entries — e.g. Fathom for trial-1
  // tenants — are honoured.
  const isProd = import.meta.env.VITE_ENV === "prod";
  const disabledFeatures = platformConfigs?.FEATURES_DISABLED_ON_PROD || [];
  const externalAdapters = allAdapters.filter(
    (a) => !isFeatureDisabledOnProd(`${a.id}Integration`, { disabledFeatures, isProd, tenant: getTenant() })
  );

  const handleTileClick = (sourceId) => {
    if (sourceId === "csvExcelUpload") {
      // Immediately proceed to file upload
      onSelectCsvExcel();
    } else {
      // Show integration interface
      setSelectedSource(sourceId);
    }
  };

  const handleCancel = () => {
    setSelectedSource(null);
    onClearSubmitError && onClearSubmitError();
  };

  const handleIntegrationSubmit = ({ apiKey, adapterOptions }) => {
    onExternalSourceSubmit({
      sourceId: selectedSource,
      apiKey,
      adapterOptions,
    });
  };

  // Get the selected adapter config
  const selectedAdapterConfig = externalAdapters.find(
    (a) => a.id === selectedSource
  );

  // If external source is selected, show its interface (tiles hidden)
  if (selectedSource && selectedAdapterConfig) {
    // Get the interface component from the registry
    const IntegrationInterface = getIntegrationInterface(selectedSource);

    return (
      <div>
        {entryPoint !== "integrations" && (
          <div style={{ marginBottom: "12px" }}>
            <ButtonGhost icon="ArrowLeft" onClick={handleCancel}>
              Back to source selection
            </ButtonGhost>
          </div>
        )}

        <IntegrationInterface
          adapterConfig={selectedAdapterConfig}
          onSubmit={handleIntegrationSubmit}
          isLoading={isLoading}
          submitError={submitError}
          onClearSubmitError={onClearSubmitError}
        />
      </div>
    );
  }

  // Show tiles for source selection
  return (
    <div>
      <UI_BODY_SEMIBOLD_SM_DF style={{ marginBottom: 16 }}>
        Select Data Source
      </UI_BODY_SEMIBOLD_SM_DF>

      <TilesContainer>
        {/* CSV/Excel Upload Tile */}
        <SourceTile
          $isSelected={false}
          onClick={() => handleTileClick("csvExcelUpload")}
        >
          <TileIcon>
            <CsvExcelIcon />
          </TileIcon>
          <TileName>CSV / Excel</TileName>
        </SourceTile>

        {/* External Adapter Tiles */}
        {externalAdapters.map((adapter) => {
          // Get the icon component from the registry
          const IntegrationIcon = getIntegrationIcon(adapter.id);

          return (
            <SourceTile
              key={adapter.id}
              $isSelected={false}
              onClick={() => handleTileClick(adapter.id)}
            >
              <TileIcon>
                {IntegrationIcon ? (
                  <IntegrationIcon size={32} />
                ) : (
                  <DefaultIconFallback name={adapter.name} />
                )}
              </TileIcon>
              <TileName>{adapter.name}</TileName>
            </SourceTile>
          );
        })}
      </TilesContainer>
    </div>
  );
};

export default DataSourceSelector;
